import { ComparableBoatData, useGetActiveCarouselItem } from '@components/CompareBoats/hooks/useCompare';
import { Grid } from 'antd';
import { KeenSliderPlugin, useKeenSlider } from 'keen-slider/react';
import { FC, useEffect } from 'react';
import styles from '../CompareTable.module.scss';
import PanelDataItem from './PanelDataItem.component';

interface DesignerPanelDataInterface {
  boatComparableData: ComparableBoatData[];
}

const MutationPlugin: KeenSliderPlugin = (slider) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      slider.update();
    });
  });

  const config = { childList: true };

  slider.on('created', () => {
    observer.observe(slider.container, config);
  });
  slider.on('destroyed', () => {
    observer.disconnect();
  });
};

const DesignerSpecsPanelData: FC<DesignerPanelDataInterface> = ({ boatComparableData }) => {
  const activeCarouselItem = useGetActiveCarouselItem();
  const { md } = Grid.useBreakpoint();

  const [ref, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      drag: false,
      slides: {
        perView: 7,
      },
      breakpoints: {
        '(max-width: 1440px)': {
          slides: { perView: 6 },
        },
        '(max-width: 1200px)': {
          slides: { perView: 5 },
        },
        '(max-width: 1024px)': {
          slides: { perView: 4 },
        },
        '(max-width: 992px)': {
          slides: { perView: 3 },
        },
        '(max-width: 768px)': {
          slides: { perView: 2 },
        },
        '(max-width: 520px)': {
          slides: { perView: 2 },
        },
      },
    },
    [MutationPlugin],
  );

  useEffect(() => {
    instanceRef?.current?.moveToIdx(activeCarouselItem);
  }, [activeCarouselItem, instanceRef]);

  const designSpecsList: { specLabel: string; specId: number; noDataSpec?: boolean }[] = [
    { specLabel: 'Naval architect', specId: 47, noDataSpec: true },
    { specLabel: 'Name/Studio', specId: 48 },
    { specLabel: 'Website', specId: 49 },
    { specLabel: 'Exterior designer', specId: 50, noDataSpec: true },
    { specLabel: 'Name/Studio', specId: 51 },
    { specLabel: 'Website', specId: 52 },
    { specLabel: 'Interior designer', specId: 53, noDataSpec: true },
    { specLabel: 'Name/Studio', specId: 54 },
    { specLabel: 'Website', specId: 55 },
  ];

  return (
    <div className={styles.specsPanelDataContainer}>
      <div style={md ? { width: 192 } : { width: '100%' }}>
        {designSpecsList.map((mainSpecItem) => (
          <div key={mainSpecItem.specId}>
            <div
              className={mainSpecItem?.noDataSpec ? styles.noDataSpecsNamesStyles : styles.specsNamesStyles}
              key={mainSpecItem.specId}
            >
              {mainSpecItem.specLabel}
            </div>

            {mainSpecItem?.specId === 48 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.designers?.at(0)?.name} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 49 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem
                  panelData={boat?.designers?.at(0)?.website ?? undefined}
                  panelDataType="link"
                  ellipsisMaxLength={20}
                  key={boat?._id}
                />
              ))}

            {mainSpecItem?.specId === 51 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.exteriorDesigners?.at(0)?.name} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 52 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem
                  panelData={boat?.exteriorDesigners?.at(0)?.website ?? undefined}
                  panelDataType="link"
                  key={boat?._id}
                />
              ))}

            {mainSpecItem?.specId === 54 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem panelData={boat?.interiorDesigners?.at(0)?.name} panelDataType="text" key={boat?._id} />
              ))}
            {mainSpecItem?.specId === 55 &&
              !md &&
              boatComparableData?.map((boat) => (
                <PanelDataItem
                  panelData={boat?.interiorDesigners?.at(0)?.website ?? undefined}
                  panelDataType="text"
                  key={boat?._id}
                />
              ))}
          </div>
        ))}
      </div>

      {md && (
        <div ref={ref} className="keen-slider">
          {boatComparableData?.map((boat) => (
            <div key={boat._id} className="keen-slider__slide">
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.designers?.at(0)?.name} panelDataType="text" />
              <PanelDataItem
                panelData={boat?.designers?.at(0)?.website ?? undefined}
                panelDataType="link"
                ellipsisMaxLength={20}
              />
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.exteriorDesigners?.at(0)?.name} panelDataType="text" />
              <PanelDataItem panelData={boat?.exteriorDesigners?.at(0)?.website ?? undefined} panelDataType="link" />
              <PanelDataItem panelData={''} panelDataType="empty" />
              <PanelDataItem panelData={boat?.interiorDesigners?.at(0)?.name} panelDataType="text" />
              <PanelDataItem panelData={boat?.interiorDesigners?.at(0)?.website ?? undefined} panelDataType="text" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesignerSpecsPanelData;
